'use client'

import { useId } from 'react'

// The Animora coin: a polished 24-carat gold coin with the Animora "A" on it (the angular A with the
// curved swoosh crossbar from the Animora logo). Use this everywhere a coin amount is shown.
//
// `size` is the coin's width/height in px. The gold, the shine and the reeded edge are drawn here as
// SVG so they stay sharp at every size (12px chips up to the 30px wallet coin). .coin-icon in
// app/globals.css only adds the soft glow and, on coins 20px and bigger, a slow glint that sweeps across.
//
// Each coin gets its own gradient ids (useId) so many coins on one screen never share or break each
// other's gradients, even when one of them sits inside a hidden panel.

const A_LEGS = 'M50 18.5 L80.5 79.5 H65.2 L50 48.6 L34.8 79.5 H19.5 Z'
const A_SWOOSH = 'M24.6 72.4 Q50 48.6 75.4 72.4 Q50 62 24.6 72.4 Z'
const SPARKLE = 'M35 19 l1.3 3.7 l3.7 1.3 l-3.7 1.3 l-1.3 3.7 l-1.3 -3.7 l-3.7 -1.3 l3.7 -1.3 z'

export function CoinIcon({ size = 16, className = '' }: { size?: number; className?: string }) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '')
  const id = (name: string) => `ac${uid}${name}`
  const detailed = size >= 24 // reeded edge + sparkle are too fine to help on tiny coins
  const glint = size >= 20

  return (
    <span
      className={`coin-icon${glint ? ' coin-glint' : ''} ${className}`.trim()}
      aria-hidden="true"
      style={{ width: size, height: size }}
    >
      <svg viewBox="0 0 100 100" focusable="false">
        <defs>
          {/* outer rim: light-dark-light bands = polished metal */}
          <linearGradient id={id('rim')} x1="12" y1="8" x2="88" y2="94" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#FFF8C4" />
            <stop offset=".14" stopColor="#FFE066" />
            <stop offset=".3" stopColor="#E8A90A" />
            <stop offset=".46" stopColor="#FFE98A" />
            <stop offset=".62" stopColor="#C98400" />
            <stop offset=".8" stopColor="#FFD54A" />
            <stop offset="1" stopColor="#A66300" />
          </linearGradient>
          {/* inner bevel: lit from the opposite side, so the face looks pressed in */}
          <linearGradient id={id('bevel')} x1="88" y1="94" x2="12" y2="8" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#FFF6B8" />
            <stop offset=".45" stopColor="#DDA00F" />
            <stop offset="1" stopColor="#8A5200" />
          </linearGradient>
          <radialGradient id={id('face')} cx="33" cy="27" r="78" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#FFF3A8" />
            <stop offset=".22" stopColor="#FFD83F" />
            <stop offset=".55" stopColor="#F2AE06" />
            <stop offset="1" stopColor="#C17A00" />
          </radialGradient>
          <linearGradient id={id('a')} x1="50" y1="18" x2="50" y2="80" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#6F3B00" />
            <stop offset="1" stopColor="#9C5A00" />
          </linearGradient>
          <linearGradient id={id('sw')} x1="50" y1="52" x2="50" y2="70" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#FFF6BE" />
            <stop offset="1" stopColor="#FFD24A" />
          </linearGradient>
          <linearGradient id={id('shine')} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#fff" stopOpacity=".9" />
            <stop offset=".4" stopColor="#fff" stopOpacity=".2" />
            <stop offset=".6" stopColor="#fff" stopOpacity="0" />
          </linearGradient>
          <clipPath id={id('clip')}>
            <circle cx="50" cy="50" r="39.5" />
          </clipPath>
        </defs>

        <circle cx="50" cy="50" r="49.2" fill="#8C5600" />
        <circle cx="50" cy="50" r="48.2" fill={`url(#${id('rim')})`} />
        {detailed && (
          <circle cx="50" cy="50" r="45" fill="none" stroke="#9A5F00" strokeOpacity=".5" strokeWidth="3.2" strokeDasharray="1 1.85" />
        )}
        <circle cx="50" cy="50" r="41.6" fill={`url(#${id('bevel')})`} />
        <circle cx="50" cy="50" r="39.6" fill={`url(#${id('face')})`} />
        <g clipPath={`url(#${id('clip')})`}>
          <ellipse cx="32" cy="24" rx="42" ry="18" transform="rotate(-36 32 24)" fill={`url(#${id('shine')})`} opacity=".5" />
        </g>

        {/* the Animora A: pale edge underneath = engraved into the gold */}
        <path d={A_LEGS} fill="#FFF6B8" opacity=".85" transform="translate(.5 1.4)" />
        <path d={A_LEGS} fill={`url(#${id('a')})`} />
        <path d={A_SWOOSH} fill={`url(#${id('sw')})`} stroke="#6F3B00" strokeWidth="1.5" strokeLinejoin="round" />
        {detailed && <path d={SPARKLE} fill="#fff" opacity=".9" />}
      </svg>
    </span>
  )
}
